
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { GeneralProjectService } from '@app/cem/project/services/general-project.service';
import { HandleDates } from '@app/common/services/handle-dates.service';
import { PlexEnvironment } from '@app/env';
import { IProjectAssetMapping } from '@app/shared/interfaces';
import { ProjectAssetMapping, ProjectWellAssetMappingView, TankBattery } from '@app/shared/models';
import { AssetsProjectAssetMappingView } from '@app/shared/models/assets-project-asset-mapping-view';
import { WellsProjectAssetMappingView } from '@app/shared/models/wells-project-asset-mapping-view';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { IModuleAllocation } from './../../models/asset-mapping-configuration';

@Injectable({ providedIn: 'root' })
export class ProjectAssetMappingService {
  private generalProjectService = inject(GeneralProjectService);
  private http = inject(HttpClient);
  private handleDates = inject(HandleDates);
  public environment = inject(PlexEnvironment);

  private mappedAssetAllocation = new BehaviorSubject<IModuleAllocation[]>([]);
  private mappedAssetAllocationHolding: IModuleAllocation[] = [];
  private clearAllocationModulesHolding: IModuleAllocation[] = [];
  private clearAllocationModules = new BehaviorSubject<IModuleAllocation[]>([]);
  private saveChangesModulesHolding: IModuleAllocation[] = [];
  private saveChangesModules = new BehaviorSubject<IModuleAllocation[]>([]);
  private cancelChangesModulesHolding: IModuleAllocation[] = [];
  private cancelChangesModules = new BehaviorSubject<IModuleAllocation[]>([]);
  public hasPendingChanges = new Subject<boolean>();
  public hasMultipleGroups = new Subject<boolean>();
  public tbAssetChanged = new BehaviorSubject<any>({});
  public tbAssetSaved = new Subject<boolean>();

  private BASE_URL = '';

  constructor() {
    this.trackModuleChanges();
    this.BASE_URL = this.environment.apiUrl;
  }

  public getProjectTotalAllocation(): Observable<number> {
    return this.mappedAssetAllocation.pipe(map(x => {
      if (x.length > 0)
        return x.map(t => t.allocationPercentage).reduce((v, i) => (v + i));
      else return 0;
    }));
  }

  public setModuleAllocation(module: IModuleAllocation) {
    const index = this.mappedAssetAllocationHolding.findIndex(x => x.assetMappingConfigurationId === module.assetMappingConfigurationId);
    if (index !== -1)
      this.mappedAssetAllocationHolding.splice(index, 1, module);
    else
      this.mappedAssetAllocationHolding.push(module);

    if (this.mappedAssetAllocation)
      this.mappedAssetAllocation.next(this.mappedAssetAllocationHolding);
  }

  public allocationIsAllowed(): Observable<boolean> {
    return this.mappedAssetAllocation.pipe(map((allocation) => {
      return (allocation.map(x => x.allocationPercentage).reduce((v, i) => (v + i))) === 1;
    }));
  }

  public getProjectModuleAllocationProperties(): Observable<IModuleAllocation[]> {
    return this.mappedAssetAllocation.pipe(map((moduleAllocation) => { return moduleAllocation; }));
  }

  public getModulesToReload(): Observable<IModuleAllocation[]> {
    return this.clearAllocationModules.pipe(map((moduleAllocation) => { return moduleAllocation; }));
  }

  public getModulesToSaveChanges(): Observable<IModuleAllocation[]> {
    return this.saveChangesModules.pipe(map((moduleAllocation) => { return moduleAllocation; }));
  }

  public getModulesToCancelChanges(): Observable<IModuleAllocation[]> {
    return this.cancelChangesModules.pipe(map((moduleAllocation) => { return moduleAllocation; }));
  }

  public toggleModuleAllocation(projectId: number, assetMappingConfigurationId: number) {
    let projectAssetMappings: IProjectAssetMapping[] = [];
    return this.generalProjectService.getProjectAssetMapping(projectId)
      .then(async (data) => {
        data.map(x => { projectAssetMappings.push(x); });
        if (projectAssetMappings.length > 0) {
          await this.generalProjectService.deleteProjectAssetMappings(projectAssetMappings);
        }
        this.setModulesToRefresh(assetMappingConfigurationId);
      });
  }

  public trackModuleChanges() {
    return this.mappedAssetAllocation.subscribe((allocation) => {
      let mod = allocation.find(x => x.hasAssets);
      this.hasPendingChanges.next(allocation.findIndex(x => x.hasPendingChange === true) !== -1);
    });
  }

  public saveChanges() {
    this.saveChangesModulesHolding = [];
    this.mappedAssetAllocationHolding.map((module) => {
      if (module.hasPendingChange) {
        this.saveChangesModulesHolding.push(module);
      }
    });
    this.saveChangesModules.next(this.saveChangesModulesHolding);
  }

  public cancelChanges() {
    this.cancelChangesModulesHolding = [];
    this.mappedAssetAllocationHolding.map((module) => {
      if (module.hasPendingChange) {
        module.hasPendingChange = false;
        this.cancelChangesModulesHolding.push(module);
      }
    });

    this.mappedAssetAllocation.next(this.mappedAssetAllocationHolding);
    this.cancelChangesModules.next(this.cancelChangesModulesHolding);
    this.hasPendingChanges.next(false);
  }

  private setModulesToRefresh(assetMappingConfigurationId: number) {
    this.clearAllocationModulesHolding = [];
    this.mappedAssetAllocationHolding.map((module) => {
      if (module.assetMappingConfigurationId !== assetMappingConfigurationId) {
        module.isActiveToggle = false;
        module.allocationPercentage = 0;
        module.hasAssets = false;
        this.clearAllocationModulesHolding.push(module);
      }
    });

    this.clearAllocationModules.next(this.clearAllocationModulesHolding);
  }

  async findMappedWells(projectId: number, assetMappingConfigurationId: number): Promise<ProjectWellAssetMappingView[]> {
    let url = `${this.BASE_URL}ProjectAssetMappingsBatch/FindMappedWells/${projectId}/${assetMappingConfigurationId}`;
    let result = await this.http.get<ProjectWellAssetMappingView[]>(url).toPromise();

    let isMultipleGroups = new Set(result.map(item => item.ActualDrillGroupReference)).size > 1;
    if (isMultipleGroups) this.hasMultipleGroups.next(isMultipleGroups);
    return this.handleDates.handleDates(result);
  }

  async findAvailableWells(projectId: number, assetMappingConfigurationId: number, tankBatteryAreaId?: number): Promise<WellsProjectAssetMappingView[]> {
    let url = `${this.BASE_URL}ProjectAssetMappingsBatch/FindAvailableWells/${projectId}/${assetMappingConfigurationId}`;
    return this.http.get<WellsProjectAssetMappingView[]>(url).toPromise().then(x => this.handleDates.handleDates(x));
  }

  async findMappedAssets(projectId: number, assetMappingConfigurationId: number): Promise<ProjectAssetMapping[]> {
    let url = `${this.BASE_URL}ProjectAssetMappingsBatch/FindMappedAssets/${projectId}/${assetMappingConfigurationId}`;
    return this.http.get<ProjectAssetMapping[]>(url).toPromise();
  }

  async findAvailableAssets(projectId: number, assetMappingConfigurationId: number): Promise<AssetsProjectAssetMappingView[]> {
    let url = `${this.BASE_URL}ProjectAssetMappingsBatch/FindAvailableAssets/${projectId}/${assetMappingConfigurationId}`;
    return this.http.get<AssetsProjectAssetMappingView[]>(url).toPromise();
  }

  async findTankBattery(assetId: number): Promise<TankBattery> {
    let url = `${this.BASE_URL}TankBatteries?$filter=AssetId eq ${assetId}&$top=1`;
    let asset: TankBattery = null;
    try {
      asset = (await this.http.get<any>(url).toPromise()).value[0];
    } catch {
      console.warn("No Tank Battery found");
    }
    return asset;
  }
}